import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Expandable } from "../components/ux/Expandable";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { quizHref, StickyCta } from "./shared";

/**
 * "Receipt" landing layout: a thermal-printer receipt itemizing what the
 * Outing actually gets you. Single CTA styled as the tip line / signature.
 * Built with coffee in mind but format-agnostic: everything is config copy.
 * MCP template name: "receipt".
 */

export interface ReceiptConfig {
  posthogEvent: string;
  audienceParam: string;
  /** "c" routes the quiz into coffee wording; omit for dinner. */
  eventTypeParam?: string;
  storeName: string;
  storeTagline: string;
  orderLabel: string;
  items: Array<{ name: string; price: string }>;
  subtotalLabel: string;
  subtotalValue: string;
  totalLabel: string;
  totalValue: string;
  tipLine: string;
  cta: string;
  ctaSub: string;
  footerLines: string[];
  faqTitle: string;
  faq: Array<{ q: string; a: string }>;
  ownedLabel?: "gay" | "lgbtq";
}

export default function LandingReceipt({ config }: { config: ReceiptConfig }) {
  const { t: tRaw } = useT("landing");

  useEffect(() => {
    tracking.trackPage(config.posthogEvent);
  }, [config.posthogEvent]);

  const ctaHref = quizHref(config.audienceParam, config.eventTypeParam);

  return (
    <div className="min-h-full bg-neutral-200 dark:bg-neutral-900 py-8 px-4 pb-28 flex flex-col items-center">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm bg-[#fffdf8] text-neutral-900 shadow-xl font-mono text-sm px-6 py-8"
        style={{
          clipPath:
            "polygon(0 0, 100% 0, 100% calc(100% - 8px), 97% 100%, 94% calc(100% - 8px), 91% 100%, 88% calc(100% - 8px), 85% 100%, 82% calc(100% - 8px), 79% 100%, 76% calc(100% - 8px), 73% 100%, 70% calc(100% - 8px), 67% 100%, 64% calc(100% - 8px), 61% 100%, 58% calc(100% - 8px), 55% 100%, 52% calc(100% - 8px), 49% 100%, 46% calc(100% - 8px), 43% 100%, 40% calc(100% - 8px), 37% 100%, 34% calc(100% - 8px), 31% 100%, 28% calc(100% - 8px), 25% 100%, 22% calc(100% - 8px), 19% 100%, 16% calc(100% - 8px), 13% 100%, 10% calc(100% - 8px), 7% 100%, 4% calc(100% - 8px), 0 100%)",
        }}
      >
        <div className="text-center">
          <p className="font-bold text-base tracking-widest uppercase">{config.storeName}</p>
          <p className="text-xs text-neutral-500 mt-1">{config.storeTagline}</p>
          <p className="text-xs text-neutral-400 mt-3">{"*".repeat(32)}</p>
          <p className="text-xs uppercase tracking-widest mt-2">{config.orderLabel}</p>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          {config.items.map((item, i) => (
            <div key={i} className="flex justify-between gap-3">
              <span className="flex-1">{item.name}</span>
              <span className="shrink-0">{item.price}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-neutral-400 mt-4">{"-".repeat(40)}</p>
        <div className="flex justify-between mt-2">
          <span>{config.subtotalLabel}</span>
          <span>{config.subtotalValue}</span>
        </div>
        <div className="flex justify-between font-bold text-base mt-1">
          <span>{config.totalLabel}</span>
          <span>{config.totalValue}</span>
        </div>

        {/* tip line CTA */}
        <div className="mt-6 border-t-2 border-dashed border-neutral-300 pt-5 text-center">
          <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">{config.tipLine}</p>
          <a
            href={ctaHref}
            className="inline-flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 text-white font-bold text-base px-10 py-3.5 w-full transition-colors shadow-lg shadow-primary-500/30 font-sans"
          >
            {config.cta}
          </a>
          <p className="text-xs text-neutral-500 mt-2 font-sans">{config.ctaSub}</p>
        </div>

        <div className="mt-6 text-center text-xs text-neutral-500 flex flex-col gap-1">
          {config.footerLines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
          <p className="mt-2 tracking-[0.4em] text-neutral-300 select-none" aria-hidden>
            ▮▯▮▮▯▮▯▮▮▯▮▮▯▮▯▮▮▯
          </p>
        </div>
      </motion.div>

      {/* FAQ below the receipt */}
      <div className="w-full max-w-sm mt-8">
        <p className="text-center text-xs uppercase tracking-widest text-neutral-400 mb-4">
          {config.faqTitle}
        </p>
        <div className="flex flex-col gap-3">
          {config.faq.map((f, i) => (
            <Expandable key={i} title={f.q}>
              {f.a}
            </Expandable>
          ))}
        </div>

        <div className="mt-10 text-center text-xs text-neutral-400 dark:text-neutral-600">
          <p className="flex items-center justify-center gap-1.5 mb-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
            {tRaw(config.ownedLabel === "lgbtq" ? "footer.proudlyLgbtqOwned" : "footer.proudlyGayOwned")}
          </p>
          <p>
            <Link href="/terms" className="underline hover:text-primary-500 transition-colors">
              {tRaw("footer.terms")}
            </Link>
            {" · "}
            <Link href="/privacy" className="underline hover:text-primary-500 transition-colors">
              {tRaw("footer.privacy")}
            </Link>
          </p>
        </div>
      </div>

      <StickyCta href={ctaHref} label={config.cta} />
    </div>
  );
}

/** Example config, rendered at /tpl/receipt and documented for MCP authors. */
export const RECEIPT_EXAMPLE: ReceiptConfig = {
  posthogEvent: "tpl_receipt_preview",
  audienceParam: "m",
  eventTypeParam: "c",
  storeName: "Outclose",
  storeTagline: "Coffee with gay men who also meant to leave the house",
  orderLabel: "Order: one Saturday, 1pm",
  items: [
    { name: "1x table of gay men, matched to your vibe", price: "incl." },
    { name: "1x conversation that skips the weather", price: "incl." },
    { name: "1x excuse to leave the apartment", price: "incl." },
    { name: "0x small talk with a stranger's dog photos", price: "0.00" },
    { name: "0x planning, herding, or group polls", price: "0.00" },
  ],
  subtotalLabel: "Subtotal: a Saturday that isn't errands",
  subtotalValue: "—",
  totalLabel: "Total",
  totalValue: "$15.99",
  tipLine: "Tip: just show up",
  cta: "Add to tab",
  ctaSub: "Take the five minute quiz. We build the table.",
  footerLines: ["Saturdays at 1pm. Select neighborhoods.", "No reservation needed. The table chat finds each other."],
  faqTitle: "Fine print",
  faq: [
    { q: "Is this a dating thing?", a: "No. Outclose is built for friendship. Tables are gay men looking for the same thing you are: people to actually know in your city." },
    { q: "What does it cost?", a: "$15.99 per Outing, or $19.99/month for the subscription with weekly Outings plus perks. Your coffee is on you." },
    { q: "How do we find each other?", a: "The table chat opens the day before with your table assignment. You coordinate how to spot each other. It's easier than it sounds." },
  ],
  ownedLabel: "gay",
};
